import type {
  IAdminService,
  AdminPaginationFilters,
  AdminProfileInfo,
} from "@/services/interfaces/IAdminService";
import type {
  AdminWorkItemKPIs,
  AdminQuestFilters,
  AdminTaskFilters,
  AdminUserFilters,
  AdminUserSummary,
  AdminAgentSummary,
  AdminUserDetail,
  AdminNotificationChannel,
  AdminChannelMutationResult,
  UserProfile,
  Quest,
  Task,
  PriceProposal,
  WorkSubmission,
  NegotiationThread,
  PaginatedResult,
  OnchainTxRow,
  LeadSource,
  UserTier,
} from "@/lib/types";
import { gradeFromFactors } from "@/lib/trustGrade/rubric";
import type { TrustGrade, TrustGradeStatsRow } from "@/lib/trustGrade/types";
import { ADMIN_STALE_THRESHOLD_DAYS, ADMIN_PAGE_SIZE } from "@/lib/constants";
import { isTaskInFlight, isTerminalQuestStatus } from "@/lib/questStatus";

const MOCK_DELAY_MS = 400;
const DAY_MS = 86_400_000;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function daysAgo(n: number): string {
  return new Date(Date.now() - n * DAY_MS).toISOString();
}

function daysFromNow(n: number): string {
  return new Date(Date.now() + n * DAY_MS).toISOString();
}

// ─── Mock Data ──────────────────────────────────────────

const MOCK_QUESTS: Quest[] = [
  {
    id: "admin-quest-001", creatorId: "user-alice", title: "Build Payment Gateway",
    description: "Implement Stripe integration for the platform.", category: "Development",
    status: "active", totalBudgetUSD: 2500, currency: "USDT", deadline: daysFromNow(21),
    attachments: [], locationRequirement: "remote", createdAt: daysAgo(10), updatedAt: daysAgo(1), taskCount: 5, completedTaskCount: 2,
  },
  {
    id: "admin-quest-002", creatorId: "user-bob", title: "Brand Redesign Campaign",
    description: "Refresh logo, color palette, and marketing assets.", category: "Design",
    status: "active", totalBudgetUSD: 1800, currency: "USDT", deadline: daysFromNow(14),
    attachments: [], locationRequirement: "remote", createdAt: daysAgo(15), updatedAt: daysAgo(8), taskCount: 4, completedTaskCount: 1,
  },
  {
    id: "admin-quest-003", creatorId: "user-carol", title: "Write API Documentation",
    description: "Document all REST endpoints with examples.", category: "Writing",
    status: "completed", totalBudgetUSD: 600, currency: "USDT", deadline: null,
    attachments: [], locationRequirement: "remote", createdAt: daysAgo(30), updatedAt: daysAgo(5), taskCount: 3, completedTaskCount: 3,
  },
  {
    id: "admin-quest-004", creatorId: "user-alice", title: "Security Audit",
    description: "Full security review of smart contracts.", category: "Research",
    status: "draft", totalBudgetUSD: 3000, currency: "USDT", deadline: daysFromNow(30),
    attachments: [], locationRequirement: "remote", createdAt: daysAgo(2), updatedAt: daysAgo(2), taskCount: 2, completedTaskCount: 0,
  },
  {
    id: "admin-quest-005", creatorId: "user-dave", title: "Mobile App Prototype",
    description: "Design and prototype the mobile experience.", category: "Design",
    status: "negotiating", totalBudgetUSD: 4000, currency: "USDT", deadline: daysFromNow(45),
    attachments: [], locationRequirement: "remote", createdAt: daysAgo(5), updatedAt: daysAgo(1), taskCount: 6, completedTaskCount: 0,
  },
  {
    id: "admin-quest-006", creatorId: "user-eve", title: "Data Pipeline Migration",
    description: "Migrate ETL pipelines to new infrastructure.", category: "Data",
    status: "active", totalBudgetUSD: 5000, currency: "USDT", deadline: daysFromNow(10),
    attachments: [], locationRequirement: "remote", createdAt: daysAgo(20), updatedAt: daysAgo(12), taskCount: 4, completedTaskCount: 0,
  },
  {
    id: "admin-quest-007", creatorId: "user-frank", title: "SEO Content Strategy",
    description: "Develop keyword research and content plan.", category: "Marketing",
    status: "published", totalBudgetUSD: 800, currency: "USDT", deadline: daysFromNow(60),
    attachments: [], locationRequirement: "remote", createdAt: daysAgo(3), updatedAt: daysAgo(3), taskCount: 3, completedTaskCount: 0,
  },
  {
    id: "admin-quest-008", creatorId: "user-bob", title: "Smart Contract Testing",
    description: "Comprehensive test suite for all protocol contracts.", category: "Development",
    status: "closed", totalBudgetUSD: 1500, currency: "USDT", deadline: null,
    attachments: [], locationRequirement: "remote", createdAt: daysAgo(45), updatedAt: daysAgo(20), taskCount: 5, completedTaskCount: 5,
  },
];

const MOCK_TASKS: Task[] = [
  // Quest 001 — active, mixed progress
  { id: "admin-task-001", questId: "admin-quest-001", parentTaskId: null, title: "Stripe SDK setup", description: "Configure Stripe SDK and environment keys.", taskType: "human", status: "completed", budgetUSD: 200, estimatedEffortHours: 3, deadline: null, skills: ["Node.js", "Stripe"], assigneeId: "user-hunter-1", order: 1, workMode: "remote", locationCountries: [], locationCities: [], createdAt: daysAgo(10), updatedAt: daysAgo(7) },
  { id: "admin-task-002", questId: "admin-quest-001", parentTaskId: null, title: "Payment intent flow", description: "Implement create/confirm payment intent endpoints.", taskType: "human", status: "completed", budgetUSD: 500, estimatedEffortHours: 8, deadline: daysFromNow(7), skills: ["Node.js", "TypeScript"], assigneeId: "user-hunter-1", order: 2, workMode: "remote", locationCountries: [], locationCities: [], createdAt: daysAgo(10), updatedAt: daysAgo(3) },
  { id: "admin-task-003", questId: "admin-quest-001", parentTaskId: null, title: "Webhook handler", description: "Handle Stripe webhooks for payment confirmation.", taskType: "human", status: "in_progress", budgetUSD: 400, estimatedEffortHours: 6, deadline: daysFromNow(10), skills: ["Node.js"], assigneeId: "user-hunter-2", order: 3, workMode: "remote", locationCountries: [], locationCities: [], createdAt: daysAgo(10), updatedAt: daysAgo(1) },
  { id: "admin-task-004", questId: "admin-quest-001", parentTaskId: null, title: "Refund logic", description: "Implement refund processing.", taskType: "human", status: "open", budgetUSD: 300, estimatedEffortHours: 4, deadline: daysFromNow(14), skills: ["Node.js", "Stripe"], assigneeId: null, order: 4, workMode: "remote", locationCountries: [], locationCities: [], createdAt: daysAgo(10), updatedAt: daysAgo(10) },
  { id: "admin-task-005", questId: "admin-quest-001", parentTaskId: null, title: "Generate test fixtures", description: "Auto-generate Stripe test data.", taskType: "ai", status: "completed", budgetUSD: 50, estimatedEffortHours: 1, deadline: null, skills: [], assigneeId: null, order: 5, workMode: "remote", locationCountries: [], locationCities: [], createdAt: daysAgo(10), updatedAt: daysAgo(8) },

  // Quest 002 — stale tasks (no activity in 8+ days)
  { id: "admin-task-006", questId: "admin-quest-002", parentTaskId: null, title: "Logo concepts", description: "Design 3 logo concepts.", taskType: "human", status: "completed", budgetUSD: 500, estimatedEffortHours: 10, deadline: null, skills: ["Graphic Design"], assigneeId: "user-hunter-3", order: 1, workMode: "remote", locationCountries: [], locationCities: [], createdAt: daysAgo(15), updatedAt: daysAgo(10) },
  { id: "admin-task-007", questId: "admin-quest-002", parentTaskId: null, title: "Color palette", description: "Define brand color palette.", taskType: "human", status: "assigned", budgetUSD: 300, estimatedEffortHours: 4, deadline: daysFromNow(5), skills: ["Graphic Design"], assigneeId: "user-hunter-3", order: 2, workMode: "remote", locationCountries: [], locationCities: [], createdAt: daysAgo(15), updatedAt: daysAgo(12) },
  { id: "admin-task-008", questId: "admin-quest-002", parentTaskId: null, title: "Marketing templates", description: "Create social media templates.", taskType: "human", status: "in_progress", budgetUSD: 400, estimatedEffortHours: 8, deadline: daysFromNow(7), skills: ["Figma", "Design"], assigneeId: "user-hunter-4", order: 3, workMode: "remote", locationCountries: [], locationCities: [], createdAt: daysAgo(15), updatedAt: daysAgo(9) },
  { id: "admin-task-009", questId: "admin-quest-002", parentTaskId: null, title: "Brand guidelines doc", description: "Write brand guidelines document.", taskType: "human", status: "open", budgetUSD: 200, estimatedEffortHours: 5, deadline: daysFromNow(10), skills: ["Writing", "Design"], assigneeId: null, order: 4, workMode: "remote", locationCountries: [], locationCities: [], createdAt: daysAgo(15), updatedAt: daysAgo(15) },

  // Quest 003 — all completed
  { id: "admin-task-010", questId: "admin-quest-003", parentTaskId: null, title: "Auth endpoints docs", description: "Document authentication endpoints.", taskType: "human", status: "completed", budgetUSD: 200, estimatedEffortHours: 4, deadline: null, skills: ["Technical Writing"], assigneeId: "user-hunter-5", order: 1, workMode: "remote", locationCountries: [], locationCities: [], createdAt: daysAgo(30), updatedAt: daysAgo(15) },
  { id: "admin-task-011", questId: "admin-quest-003", parentTaskId: null, title: "Quest API docs", description: "Document quest CRUD endpoints.", taskType: "human", status: "completed", budgetUSD: 200, estimatedEffortHours: 4, deadline: null, skills: ["Technical Writing"], assigneeId: "user-hunter-5", order: 2, workMode: "remote", locationCountries: [], locationCities: [], createdAt: daysAgo(30), updatedAt: daysAgo(10) },
  { id: "admin-task-012", questId: "admin-quest-003", parentTaskId: null, title: "API examples", description: "Add curl/JS examples to each endpoint.", taskType: "ai", status: "completed", budgetUSD: 50, estimatedEffortHours: 2, deadline: null, skills: [], assigneeId: null, order: 3, workMode: "remote", locationCountries: [], locationCities: [], createdAt: daysAgo(30), updatedAt: daysAgo(8) },

  // Quest 005 — negotiating, all open/unassigned
  { id: "admin-task-013", questId: "admin-quest-005", parentTaskId: null, title: "User research", description: "Conduct user interviews.", taskType: "human", status: "open", budgetUSD: 600, estimatedEffortHours: 12, deadline: daysFromNow(20), skills: ["UX Research"], assigneeId: null, order: 1, workMode: "remote", locationCountries: [], locationCities: [], createdAt: daysAgo(5), updatedAt: daysAgo(5) },
  { id: "admin-task-014", questId: "admin-quest-005", parentTaskId: null, title: "Wireframes", description: "Create wireframes for all screens.", taskType: "human", status: "open", budgetUSD: 800, estimatedEffortHours: 16, deadline: daysFromNow(30), skills: ["Figma", "UX"], assigneeId: null, order: 2, workMode: "remote", locationCountries: [], locationCities: [], createdAt: daysAgo(5), updatedAt: daysAgo(5) },

  // Quest 006 — active but completely stale (all tasks stuck)
  { id: "admin-task-015", questId: "admin-quest-006", parentTaskId: null, title: "Schema migration", description: "Migrate database schemas.", taskType: "human", status: "in_progress", budgetUSD: 1500, estimatedEffortHours: 20, deadline: daysFromNow(5), skills: ["PostgreSQL", "ETL"], assigneeId: "user-hunter-6", order: 1, workMode: "remote", locationCountries: [], locationCities: [], createdAt: daysAgo(20), updatedAt: daysAgo(14) },
  { id: "admin-task-016", questId: "admin-quest-006", parentTaskId: null, title: "Data validation", description: "Validate migrated data integrity.", taskType: "human", status: "assigned", budgetUSD: 800, estimatedEffortHours: 10, deadline: daysFromNow(8), skills: ["SQL", "Python"], assigneeId: "user-hunter-6", order: 2, workMode: "remote", locationCountries: [], locationCities: [], createdAt: daysAgo(20), updatedAt: daysAgo(12) },
  { id: "admin-task-017", questId: "admin-quest-006", parentTaskId: null, title: "Pipeline scripts", description: "Write new ETL scripts.", taskType: "human", status: "open", budgetUSD: 1000, estimatedEffortHours: 15, deadline: daysFromNow(10), skills: ["Python", "ETL"], assigneeId: null, order: 3, workMode: "remote", locationCountries: [], locationCities: [], createdAt: daysAgo(20), updatedAt: daysAgo(20) },

  // Submitted tasks (pending review)
  { id: "admin-task-018", questId: "admin-quest-001", parentTaskId: null, title: "Payment UI components", description: "Frontend components for checkout.", taskType: "human", status: "submitted", budgetUSD: 600, estimatedEffortHours: 10, deadline: daysFromNow(5), skills: ["React", "TypeScript"], assigneeId: "user-hunter-2", order: 6, workMode: "remote", locationCountries: [], locationCities: [], createdAt: daysAgo(8), updatedAt: daysAgo(2) },

  // Disputed task
  { id: "admin-task-019", questId: "admin-quest-002", parentTaskId: null, title: "Icon set design", description: "Design custom icon set.", taskType: "human", status: "disputed", budgetUSD: 400, estimatedEffortHours: 8, deadline: daysAgo(2), skills: ["Illustration"], assigneeId: "user-hunter-7", order: 5, workMode: "remote", locationCountries: [], locationCities: [], createdAt: daysAgo(15), updatedAt: daysAgo(1) },
];

/** Map quest IDs to chain IDs for filtering (testnet=80002, mainnet=137) */
const MOCK_QUEST_CHAINS: Record<string, number> = {
  "admin-quest-001": 80002,
  "admin-quest-002": 80002,
  "admin-quest-003": 80002,
  "admin-quest-004": 137,
  "admin-quest-005": 137,
  "admin-quest-006": 80002,
  "admin-quest-007": 137,
  "admin-quest-008": 80002,
};

const MOCK_SUBMISSIONS: WorkSubmission[] = [
  {
    id: "admin-sub-001", taskId: "admin-task-018", submitterId: "user-hunter-2",
    description: "Payment UI components ready for review. All Stripe elements integrated.",
    attachments: [], submittedAt: daysAgo(2), status: "pending", feedback: null,
  },
  {
    id: "admin-sub-002", taskId: "admin-task-007", submitterId: "user-hunter-3",
    description: "Color palette v1 submitted.",
    attachments: [], submittedAt: daysAgo(4), status: "pending", feedback: null,
  },
];

const MOCK_NEGOTIATIONS: NegotiationThread[] = [
  {
    taskId: "admin-task-013", workerId: "user-hunter-8", workerName: "Hunter Eight",
    workerAvatarUrl: null, currentStatus: "pending", latestPrice: 550, agreedPrice: null,
    proposals: [
      { id: "prop-001", taskId: "admin-task-013", questId: "admin-quest-005", workerId: "user-hunter-8", proposedPrice: 500, message: "I can do the research for $500.", status: "countered", parentProposalId: null, round: 1, proposedBy: "worker", createdAt: daysAgo(4), updatedAt: daysAgo(3) },
      { id: "prop-002", taskId: "admin-task-013", questId: "admin-quest-005", workerId: "user-hunter-8", proposedPrice: 600, message: "Budget is $600, can you meet in the middle?", status: "countered", parentProposalId: "prop-001", round: 2, proposedBy: "creator", createdAt: daysAgo(3), updatedAt: daysAgo(2) },
      { id: "prop-003", taskId: "admin-task-013", questId: "admin-quest-005", workerId: "user-hunter-8", proposedPrice: 550, message: "How about $550?", status: "pending", parentProposalId: "prop-002", round: 3, proposedBy: "worker", createdAt: daysAgo(2), updatedAt: daysAgo(2) },
    ],
  },
  {
    taskId: "admin-task-014", workerId: "user-hunter-9", workerName: "Hunter Nine",
    workerAvatarUrl: null, currentStatus: "pending", latestPrice: 700, agreedPrice: null,
    proposals: [
      { id: "prop-004", taskId: "admin-task-014", questId: "admin-quest-005", workerId: "user-hunter-9", proposedPrice: 700, message: "I can do wireframes for $700.", status: "pending", parentProposalId: null, round: 1, proposedBy: "worker", createdAt: daysAgo(3), updatedAt: daysAgo(3) },
    ],
  },
];

const MOCK_CREATOR_NAMES: Record<string, string> = {
  "user-alice": "Alice Chen",
  "user-bob": "Bob Martinez",
  "user-carol": "Carol Wright",
  "user-dave": "Dave Kim",
  "user-eve": "Eve Johnson",
  "user-frank": "Frank Lopez",
};

const MOCK_ASSIGNEE_NAMES: Record<string, string> = {
  "user-hunter-1": "Hunter Alpha",
  "user-hunter-2": "Hunter Beta",
  "user-hunter-3": "Hunter Gamma",
  "user-hunter-4": "Hunter Delta",
  "user-hunter-5": "Hunter Epsilon",
  "user-hunter-6": "Hunter Zeta",
  "user-hunter-7": "Hunter Eta",
  "user-hunter-8": "Hunter Eight",
  "user-hunter-9": "Hunter Nine",
};

/**
 * Which mock profiles arrived as leads, and through which door. Everyone else
 * signed up on their own — the majority case, and the one that renders no badge.
 * Spread across all four sources so every option of the origin filter returns
 * something in mock mode.
 */
const MOCK_LEAD_ORIGINS: Record<string, LeadSource> = {
  "user-frank": "outreach",
  "user-hunter-1": "candidate",
  "user-dave": "manual",
  "user-hunter-2": "csv",
};

const MOCK_USER_PROFILES: Record<string, UserProfile> = {
  "user-alice": {
    id: "user-alice", handle: "alice", displayName: "Alice Chen", email: "alice@wexare.test",
    avatarUrl: null, bio: "Full-stack developer", skills: ["TypeScript", "React", "Node.js"],
    needs: ["Design help"], walletAddress: "0xAlice", rolePreference: "creator",
    activityLevel: "active", preferredCurrency: "USDT", preferredLanguage: "en",
    recurringMatch: true,
    reputation: { overall: 92, completionRate: 95, qualityScore: 90, speedScore: 88, reliability: 94 },
    role: "admin", locationCountry: null, locationCity: null, createdAt: daysAgo(60),
  },
  "user-bob": {
    id: "user-bob", handle: "bob", displayName: "Bob Martinez", email: "bob@wexare.test",
    avatarUrl: null, bio: "Designer and brand strategist", skills: ["Figma", "Branding"],
    needs: ["Development"], walletAddress: "0xBob", rolePreference: "creator",
    activityLevel: "active", preferredCurrency: "USDT", preferredLanguage: "es",
    recurringMatch: true,
    reputation: { overall: 85, completionRate: 80, qualityScore: 90, speedScore: 82, reliability: 88 },
    role: "user", locationCountry: null, locationCity: null, createdAt: daysAgo(55),
  },
  "user-carol": {
    id: "user-carol", handle: "carol", displayName: "Carol Wright", email: "carol@wexare.test",
    avatarUrl: null, bio: "Technical writer", skills: ["Documentation", "API Design"],
    needs: [], walletAddress: null, rolePreference: "creator",
    activityLevel: "casual", preferredCurrency: "USDT", preferredLanguage: "en",
    recurringMatch: true,
    reputation: { overall: 78, completionRate: 85, qualityScore: 80, speedScore: 70, reliability: 76 },
    role: "user", locationCountry: null, locationCity: null, createdAt: daysAgo(50),
  },
  "user-dave": {
    id: "user-dave", handle: "dave", displayName: "Dave Kim", email: "dave@wexare.test",
    avatarUrl: null, bio: "Product designer", skills: ["UX Research", "Figma", "Prototyping"],
    needs: ["Backend dev"], walletAddress: "0xDave", rolePreference: "both",
    activityLevel: "active", preferredCurrency: "USDT", preferredLanguage: "en",
    recurringMatch: true,
    reputation: { overall: 88, completionRate: 90, qualityScore: 85, speedScore: 90, reliability: 87 },
    role: "user", locationCountry: null, locationCity: null, createdAt: daysAgo(40),
  },
  "user-eve": {
    id: "user-eve", handle: "eve", displayName: "Eve Johnson", email: "eve@wexare.test",
    avatarUrl: null, bio: "Data engineer", skills: ["PostgreSQL", "ETL", "Python"],
    needs: [], walletAddress: "0xEve", rolePreference: "creator",
    activityLevel: "core", preferredCurrency: "USDT", preferredLanguage: "en",
    recurringMatch: true,
    reputation: { overall: 90, completionRate: 88, qualityScore: 92, speedScore: 85, reliability: 95 },
    role: "user", locationCountry: null, locationCity: null, createdAt: daysAgo(35),
  },
  "user-frank": {
    id: "user-frank", handle: "frank", displayName: "Frank Lopez", email: "frank@wexare.test",
    avatarUrl: null, bio: "SEO and marketing", skills: ["SEO", "Content Strategy"],
    needs: ["Design"], walletAddress: null, rolePreference: "creator",
    activityLevel: "casual", preferredCurrency: "USDT", preferredLanguage: "es",
    recurringMatch: true,
    reputation: { overall: 70, completionRate: 75, qualityScore: 72, speedScore: 65, reliability: 68 },
    role: "user", locationCountry: null, locationCity: null, createdAt: daysAgo(20),
  },
  "user-hunter-1": {
    id: "user-hunter-1", handle: "halpha", displayName: "Hunter Alpha", email: "hunter1@wexare.test",
    avatarUrl: null, bio: "Stripe integration specialist", skills: ["Node.js", "Stripe", "TypeScript"],
    needs: [], walletAddress: "0xH1", rolePreference: "hunter",
    activityLevel: "active", preferredCurrency: "USDT", preferredLanguage: "en",
    recurringMatch: true,
    reputation: { overall: 91, completionRate: 95, qualityScore: 88, speedScore: 92, reliability: 90 },
    role: "user", locationCountry: null, locationCity: null, createdAt: daysAgo(45),
  },
  "user-hunter-2": {
    id: "user-hunter-2", handle: "hbeta", displayName: "Hunter Beta", email: "hunter2@wexare.test",
    avatarUrl: null, bio: "Frontend developer", skills: ["React", "TypeScript"],
    needs: [], walletAddress: "0xH2", rolePreference: "hunter",
    activityLevel: "active", preferredCurrency: "USDT", preferredLanguage: "pt",
    recurringMatch: true,
    reputation: { overall: 83, completionRate: 80, qualityScore: 85, speedScore: 82, reliability: 84 },
    role: "user", locationCountry: null, locationCity: null, createdAt: daysAgo(42),
  },
  "user-hunter-3": {
    id: "user-hunter-3", handle: "hgamma", displayName: "Hunter Gamma", email: "hunter3@wexare.test",
    avatarUrl: null, bio: "Graphic designer", skills: ["Graphic Design", "Illustration"],
    needs: [], walletAddress: "0xH3", rolePreference: "hunter",
    activityLevel: "active", preferredCurrency: "USDT", preferredLanguage: "en",
    recurringMatch: true,
    reputation: { overall: 87, completionRate: 88, qualityScore: 90, speedScore: 80, reliability: 89 },
    role: "user", locationCountry: null, locationCity: null, createdAt: daysAgo(38),
  },
};

// ─── Helpers ────────────────────────────────────────────

function paginate<T>(
  items: T[],
  cursor: string | undefined,
  limit: number,
): PaginatedResult<T> {
  let startIndex = 0;
  if (cursor) {
    const cursorIdx = parseInt(cursor, 10);
    if (!Number.isNaN(cursorIdx)) startIndex = cursorIdx;
  }
  const page = items.slice(startIndex, startIndex + limit);
  const nextIndex = startIndex + limit;
  return {
    data: page,
    total: items.length,
    cursor: nextIndex < items.length ? String(nextIndex) : null,
    hasMore: nextIndex < items.length,
  };
}

function isStale(updatedAt: string): boolean {
  const diffMs = Date.now() - new Date(updatedAt).getTime();
  return diffMs > ADMIN_STALE_THRESHOLD_DAYS * DAY_MS;
}

// ─── Service ────────────────────────────────────────────

export class MockAdminService implements IAdminService {
  private readonly notificationChannels = new Map<string, AdminNotificationChannel[]>();

  /** Expose creator names for UI display */
  static readonly CREATOR_NAMES = MOCK_CREATOR_NAMES;
  /** Expose assignee names for UI display */
  static readonly ASSIGNEE_NAMES = MOCK_ASSIGNEE_NAMES;

  async resolveProfiles(
    userIds: string[],
  ): Promise<Map<string, AdminProfileInfo>> {
    await delay(MOCK_DELAY_MS);
    const allNames: Record<string, string> = {
      ...MOCK_CREATOR_NAMES,
      ...MOCK_ASSIGNEE_NAMES,
    };
    const map = new Map<string, AdminProfileInfo>();
    for (const id of new Set(userIds.filter(Boolean))) {
      map.set(id, {
        displayName: allNames[id] ?? id,
        email: allNames[id] ? `${id}@wexare.test` : "",
      });
    }
    return map;
  }

  async resolveTaskTitles(
    taskIds: string[],
  ): Promise<Map<string, string>> {
    await delay(MOCK_DELAY_MS);
    const map = new Map<string, string>();
    for (const id of new Set(taskIds.filter(Boolean))) {
      const task = MOCK_TASKS.find((t) => t.id === id);
      map.set(id, task?.title ?? id);
    }
    return map;
  }

  private questsForChain(chainId?: number): Quest[] {
    if (!chainId) return MOCK_QUESTS;
    return MOCK_QUESTS.filter((q) => MOCK_QUEST_CHAINS[q.id] === chainId);
  }

  private tasksForChain(chainId?: number): Task[] {
    const questIds = new Set(this.questsForChain(chainId).map((q) => q.id));
    return MOCK_TASKS.filter((t) => questIds.has(t.questId));
  }

  async getWorkItemKPIs(chainId?: number): Promise<AdminWorkItemKPIs> {
    await delay(MOCK_DELAY_MS);

    const quests = this.questsForChain(chainId);
    const tasks = this.tasksForChain(chainId);

    const totalQuests = quests.length;
    const activeQuests = quests.filter((q) => q.status === "active").length;
    const totalTasks = tasks.length;
    const openTasks = tasks.filter((t) => t.status === "open").length;
    const unassignedTasks = tasks.filter(
      (t) => t.status === "open" && !t.assigneeId,
    ).length;
    const staleTasks = tasks.filter(
      (t) =>
        (t.status === "assigned" || t.status === "in_progress") &&
        isStale(t.updatedAt),
    ).length;
    const pendingReviewTasks = tasks.filter(
      (t) => t.status === "submitted",
    ).length;
    const disputedTasks = tasks.filter(
      (t) => t.status === "disputed",
    ).length;
    const completedTasks = tasks.filter(
      (t) => t.status === "completed",
    ).length;
    const completionRate =
      totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

    // User counts
    const userIds = new Set<string>();
    const activeUserIds = new Set<string>();
    const thirtyDaysAgo = Date.now() - 30 * DAY_MS;

    for (const q of quests) {
      userIds.add(q.creatorId);
      if (new Date(q.updatedAt).getTime() >= thirtyDaysAgo) activeUserIds.add(q.creatorId);
    }
    for (const t of tasks) {
      if (t.assigneeId) {
        userIds.add(t.assigneeId);
        if (new Date(t.updatedAt).getTime() >= thirtyDaysAgo) activeUserIds.add(t.assigneeId);
      }
    }

    return {
      totalQuests,
      activeQuests,
      totalTasks,
      openTasks,
      unassignedTasks,
      staleTasks,
      pendingReviewTasks,
      disputedTasks,
      completionRate,
      totalUsers: userIds.size,
      activeUsers: activeUserIds.size,
    };
  }

  async listAllQuests(
    filters: AdminQuestFilters,
  ): Promise<PaginatedResult<Quest>> {
    await delay(MOCK_DELAY_MS);

    let result = this.questsForChain(filters.chainId);

    if (filters.noLocation) {
      result = result.filter((_, i) => i % 4 === 0);
    }
    if (filters.status) {
      result = result.filter((q) => q.status === filters.status);
    }
    if (filters.search) {
      const s = filters.search.toLowerCase();
      result = result.filter(
        (q) =>
          q.title.toLowerCase().includes(s) ||
          q.description.toLowerCase().includes(s),
      );
    }

    result.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

    return paginate(result, filters.cursor, filters.limit ?? ADMIN_PAGE_SIZE);
  }

  async listAllTasks(
    filters: AdminTaskFilters,
  ): Promise<PaginatedResult<Task>> {
    await delay(MOCK_DELAY_MS);

    let result = this.tasksForChain(filters.chainId);

    if (filters.status) {
      result = result.filter((t) => t.status === filters.status);
    }
    if (filters.taskType) {
      result = result.filter((t) => t.taskType === filters.taskType);
    }
    if (filters.assigneePresence === "unassigned") {
      result = result.filter((t) => !t.assigneeId);
    } else if (filters.assigneePresence === "assigned") {
      result = result.filter((t) => !!t.assigneeId);
    }
    if (filters.staleOnly) {
      result = result.filter(
        (t) =>
          (t.status === "assigned" || t.status === "in_progress") &&
          isStale(t.updatedAt),
      );
    }

    result.sort(
      (a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    );

    return paginate(result, filters.cursor, filters.limit ?? ADMIN_PAGE_SIZE);
  }

  async setTaskTrustGrade(taskId: string, grade: TrustGrade | null, reason: string): Promise<void> {
    // Same refusals as admin_set_task_trust_grade, so mock mode can't hide a missing reason.
    if (!reason.trim()) throw new Error("A reason is required");
    const task = MOCK_TASKS.find((t) => t.id === taskId);
    if (!task) throw new Error("Task not found");
    task.trustGrade = grade ?? gradeFromFactors(task.trustFactors ?? {}, task.budgetUSD).grade;
    task.trustGradeOverridden = grade !== null;
  }

  async setUserTier(userId: string, tier: UserTier): Promise<void> {
    await delay(MOCK_DELAY_MS);
    const profile = MOCK_USER_PROFILES[userId];
    if (!profile) throw new Error("user_not_found");
    // A new object, never an in-place write: React Query keeps the old render
    // when a refetch hands back the same reference.
    MOCK_USER_PROFILES[userId] = { ...profile, tier, tierSetAt: new Date().toISOString(), tierSetByName: "Dev User" };
  }

  async getTrustGradeStats(_chainId: number | null): Promise<TrustGradeStatsRow[]> {
    // Status counts sum to taskCount, so completion stays within 0–100%.
    return [4, 3, 2, 1, 0].map((grade) => {
      const doneCount = 1 + grade;
      const cancelledCount = grade === 4 ? 1 : 0;
      const disputedCount = grade <= 1 ? 1 : 0;
      return {
        grade: grade as TrustGrade, taskCount: 3 + doneCount + cancelledCount + disputedCount, openCount: 2, inProgressCount: 1, doneCount,
        cancelledCount, disputedCount, medianHoursToFirstProposal: 6 + grade,
        avgBudgetUsd: 100 * (5 - grade), creatorEditedCount: 1, heuristicCount: 2, overriddenCount: grade === 0 ? 1 : 0,
        overridesUp: grade === 0 ? 1 : 0, overridesDown: 0,
      };
    });
  }

  async listPendingReviews(
    filters: AdminPaginationFilters,
  ): Promise<PaginatedResult<WorkSubmission>> {
    await delay(MOCK_DELAY_MS);

    const taskIds = new Set(this.tasksForChain(filters.chainId).map((t) => t.id));
    const pending = MOCK_SUBMISSIONS
      .filter((s) => s.status === "pending" && taskIds.has(s.taskId))
      .sort(
        (a, b) =>
          new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime(),
      );

    return paginate(pending, filters.cursor, filters.limit ?? ADMIN_PAGE_SIZE);
  }

  async listStuckNegotiations(
    filters: AdminPaginationFilters,
  ): Promise<PaginatedResult<NegotiationThread>> {
    await delay(MOCK_DELAY_MS);

    const taskIds = new Set(this.tasksForChain(filters.chainId).map((t) => t.id));
    const stuck = MOCK_NEGOTIATIONS.filter(
      (n) => n.currentStatus === "pending" && taskIds.has(n.taskId),
    );

    return paginate(stuck, filters.cursor, filters.limit ?? ADMIN_PAGE_SIZE);
  }

  // ─── User management ──────────────────────────────────

  async listUsers(
    filters: AdminUserFilters,
  ): Promise<PaginatedResult<AdminUserSummary>> {
    await delay(MOCK_DELAY_MS);

    const quests = this.questsForChain(filters.chainId);
    const tasks = this.tasksForChain(filters.chainId);
    const chainQuestIds = new Set(quests.map((q) => q.id));

    // EVERY profile, not the ones with on-chain activity — mirroring
    // admin_list_users_by_chain since it stopped being a membership test. The
    // old mock collected creators ∪ assignees ∪ negotiation workers, which is
    // exactly the union that hid a worker who had only applied, so reproducing
    // it here would hide the bug from mock-mode review too.
    const userIds = new Set<string>(Object.keys(MOCK_USER_PROFILES));

    let users: AdminUserSummary[] = [...userIds].map((uid) => {
      const profile = MOCK_USER_PROFILES[uid];
      const questCount = quests.filter((q) => q.creatorId === uid).length;
      const taskCount = tasks.filter((t) => t.assigneeId === uid).length;
      const proposalCount = MOCK_NEGOTIATIONS.filter(
        (n) => n.workerId === uid && chainQuestIds.has(n.proposals[0]?.questId ?? ""),
      ).length;

      return {
        id: uid,
        displayName: profile?.displayName ?? MOCK_CREATOR_NAMES[uid] ?? MOCK_ASSIGNEE_NAMES[uid] ?? uid,
        handle: profile?.handle ?? uid,
        email: profile?.email ?? `${uid}@wexare.test`,
        avatarUrl: profile?.avatarUrl ?? null,
        rolePreference: profile?.rolePreference ?? "both" as const,
        role: profile?.role ?? "user" as const,
        createdAt: profile?.createdAt ?? daysAgo(30),
        questCount,
        taskCount,
        proposalCount,
        // The mock has no candidate rows to count against, so this is honestly
        // zero rather than a plausible-looking number. What it does exercise is
        // the column and the profile-first listing.
        applicationCount: 0,
        leadSource: MOCK_LEAD_ORIGINS[uid] ?? null,
        leadClaimedAt: MOCK_LEAD_ORIGINS[uid] ? daysAgo(12) : null,
        tier: profile?.tier ?? "pro",
      };
    });

    if (filters.origin === "lead") {
      users = users.filter(
        (u) => u.leadSource !== null && (!filters.leadSource || u.leadSource === filters.leadSource),
      );
    } else if (filters.origin === "signup") {
      users = users.filter((u) => u.leadSource === null);
    }

    if (filters.activeOnly) {
      users = users.filter(
        (u) => u.questCount + u.taskCount + u.proposalCount + u.applicationCount > 0,
      );
    }

    if (filters.tier) {
      users = users.filter((u) => (MOCK_USER_PROFILES[u.id]?.tier ?? "pro") === filters.tier);
    }

    if (filters.noLocation) {
      users = users.filter((_, i) => i % 3 === 0);
    }

    // Apply search filter
    if (filters.search) {
      const s = filters.search.toLowerCase();
      users = users.filter(
        (u) =>
          u.displayName.toLowerCase().includes(s) ||
          u.handle.toLowerCase().includes(s) ||
          u.email.toLowerCase().includes(s),
      );
    }

    // Sort by created date descending
    users.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

    return paginate(users, filters.cursor, filters.limit ?? ADMIN_PAGE_SIZE);
  }

  async getUserDetail(userId: string, chainId?: number): Promise<AdminUserDetail> {
    await delay(MOCK_DELAY_MS);

    const profile = MOCK_USER_PROFILES[userId];
    if (!profile) {
      throw new Error(`User not found: ${userId}`);
    }

    const quests = this.questsForChain(chainId);

    const createdQuests = quests.filter((q) => q.creatorId === userId);
    const totalBudgeted = createdQuests.reduce((sum, q) => sum + q.totalBudgetUSD, 0);

    // Use accepted proposal prices (not task budgets) for paid/earned
    const createdQuestIds = new Set(createdQuests.map((q) => q.id));
    let totalPaid = 0;
    let totalEarned = 0;
    for (const n of MOCK_NEGOTIATIONS) {
      for (const p of n.proposals) {
        if (p.status !== "accepted") continue;
        if (createdQuestIds.has(p.questId)) totalPaid += p.proposedPrice;
        if (n.workerId === userId) totalEarned += p.proposedPrice;
      }
    }

    return {
      profile,
      walletAddress: profile.walletAddress,
      chainRegistered: profile.walletAddress ? 80002 : null,
      isOnboarded: true,
      totalBudgeted,
      totalPaid,
      totalEarned,
    };
  }

  async getUserQuests(
    userId: string,
    filters: AdminPaginationFilters,
  ): Promise<PaginatedResult<Quest>> {
    await delay(MOCK_DELAY_MS);

    const quests = this.questsForChain(filters.chainId)
      .filter((q) => q.creatorId === userId)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    return paginate(quests, filters.cursor, filters.limit ?? ADMIN_PAGE_SIZE);
  }

  async getUserTasks(
    userId: string,
    filters: AdminPaginationFilters,
  ): Promise<PaginatedResult<Task>> {
    await delay(MOCK_DELAY_MS);

    const tasks = this.tasksForChain(filters.chainId)
      .filter((t) => t.assigneeId === userId)
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

    return paginate(tasks, filters.cursor, filters.limit ?? ADMIN_PAGE_SIZE);
  }

  async getUserProposals(
    userId: string,
    filters: AdminPaginationFilters,
  ): Promise<PaginatedResult<PriceProposal>> {
    await delay(MOCK_DELAY_MS);

    const chainQuestIds = new Set(this.questsForChain(filters.chainId).map((q) => q.id));
    const allProposals: PriceProposal[] = [];

    for (const n of MOCK_NEGOTIATIONS) {
      if (n.workerId === userId) {
        for (const p of n.proposals) {
          if (!filters.chainId || chainQuestIds.has(p.questId)) {
            allProposals.push(p);
          }
        }
      }
    }

    allProposals.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

    return paginate(allProposals, filters.cursor, filters.limit ?? ADMIN_PAGE_SIZE);
  }

  // ─── On-chain tx log (mock: noop log, empty list) ─────
  async logOnchainTx(): Promise<void> {
    // Mock mode doesn't issue on-chain txs.
  }

  async updateOnchainTxReceipt(): Promise<void> {
    // Mock mode doesn't issue on-chain txs.
  }

  async listOnchainTxs(): Promise<PaginatedResult<OnchainTxRow>> {
    return { data: [], total: 0, cursor: null, hasMore: false };
  }

  async getOnchainTx(): Promise<OnchainTxRow | null> {
    return null;
  }

  async resolveChainQuestTitles() {
    return new Map<string, { id: string; title: string }>();
  }

  async resolveChainTaskTitles() {
    return new Map<string, { id: string; title: string; questId: string }>();
  }

  async resolveProfilesByWallet() {
    return new Map<string, { privyDid: string; displayName: string; email: string }>();
  }

  // Intentionally ignores _chainId — users are not chain-scoped in the mock
  // (the real SQL filters by chain membership; the mock returns a fixed set).
  async getMapUsers(_chainId?: number) {
    return [
      { city: "New York",      country: "United States",  count: 14 },
      { city: "London",        country: "United Kingdom", count: 9  },
      { city: "Paris",         country: "France",         count: 6  },
      { city: "Tokyo",         country: "Japan",          count: 11 },
      { city: "São Paulo",     country: "Brazil",         count: 5  },
      { city: "Mumbai",        country: "India",          count: 8  },
      { city: "Berlin",        country: "Germany",        count: 7  },
      { city: "Sydney",        country: "Australia",      count: 4  },
      { city: "San Francisco", country: "United States",  count: 12 },
      { city: "Singapore",     country: "Singapore",      count: 5  },
    ];
  }

  async getMapQuests(chainId?: number) {
    const all = [
      { city: "New York",      country: "United States",  count: 4, chain: 80002 },
      { city: "London",        country: "United Kingdom", count: 3, chain: 80002 },
      { city: "San Francisco", country: "United States",  count: 6, chain: 137   },
      { city: "Paris",         country: "France",         count: 2, chain: 137   },
      { city: "Tokyo",         country: "Japan",          count: 5, chain: 80002 },
      { city: "Singapore",     country: "Singapore",      count: 3, chain: 137   },
      { city: "Dubai",         country: "UAE",            count: 2, chain: 80002 },
    ];
    const filtered = chainId !== undefined ? all.filter((r) => r.chain === chainId) : all;
    return filtered.map(({ city, country, count }) => ({ city, country, count }));
  }

  async getMapProspects(chainId?: number) {
    const all = [
      { city: "New York",      country: "United States",  count: 7,  chain: 80002 },
      { city: "London",        country: "United Kingdom", count: 5,  chain: 80002 },
      { city: "Mumbai",        country: "India",          count: 10, chain: 137   },
      { city: "San Francisco", country: "United States",  count: 8,  chain: 137   },
      { city: "Sydney",        country: "Australia",      count: 3,  chain: 80002 },
      { city: "Berlin",        country: "Germany",        count: 4,  chain: 137   },
      { city: "Toronto",       country: "Canada",         count: 6,  chain: 80002 },
      { city: "Lagos",         country: "Nigeria",        count: 2,  chain: 137   },
    ];
    const filtered = chainId !== undefined ? all.filter((r) => r.chain === chainId) : all;
    return filtered.map(({ city, country, count }) => ({ city, country, count }));
  }

  async getMapUserStats() {
    const profiles = Object.values(MOCK_USER_PROFILES);

    const roleCounts: Record<string, number> = {};
    for (const p of profiles) {
      roleCounts[p.rolePreference] = (roleCounts[p.rolePreference] ?? 0) + 1;
    }
    const rolePreference = Object.entries(roleCounts)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value);

    const skillCounts: Record<string, number> = {};
    for (const p of profiles) {
      for (const s of p.skills) {
        skillCounts[s] = (skillCounts[s] ?? 0) + 1;
      }
    }
    const topSkills = Object.entries(skillCounts)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);

    return { rolePreference, topSkills };
  }

  async listAgents(): Promise<AdminAgentSummary[]> {
    return [
      {
        agentId: "mock-agent-1", agentDid: "agent-dev-1", name: "Scout", status: "active",
        ownerId: "user-dev-1", ownerName: "Dev User",
        createdAt: "2026-10-01T09:00:00Z", lastActivityAt: "2026-10-09T08:30:00Z",
      },
      {
        agentId: "mock-agent-2", agentDid: "agent-dev-2", name: "Pilot", status: "paused",
        ownerId: "user-dev-2", ownerName: "Dev Two",
        createdAt: "2026-10-03T09:00:00Z", lastActivityAt: null,
      },
    ];
  }

  async getMapNoLocationCounts(_chainId?: number) {
    return { users: 12, quests: 4, prospects: 7 };
  }

  async getMapQuestStats(chainId?: number) {
    const statusCounts: Record<string, number> = {};
    const categoryCounts: Record<string, number> = {};

    const quests = chainId !== undefined ? this.questsForChain(chainId) : MOCK_QUESTS;
    for (const q of quests) {
      statusCounts[q.status] = (statusCounts[q.status] ?? 0) + 1;
      categoryCounts[q.category] = (categoryCounts[q.category] ?? 0) + 1;
    }

    const statusOrder = ["active", "published", "draft", "negotiating", "completed", "closed"];
    const statusList = statusOrder
      .filter((s) => statusCounts[s])
      .map((s) => ({ label: s, value: statusCounts[s] ?? 0 }));

    const topCategories = Object.entries(categoryCounts)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value);

    return { statusCounts: statusList, topCategories };
  }

  // ─── Quest status transitions (admin-only, mock) ──────

  // Mirrors assert_quest_admin_terminable: an admin may only end a quest that
  // holds no on-chain escrow and has no work in flight. Kept in step with the
  // SQL deliberately -- a mock that is more permissive than the server teaches
  // the wrong lesson in demo mode.
  private assertTerminable(questId: string): Quest {
    const quest = MOCK_QUESTS.find((q) => q.id === questId);
    if (!quest) throw new Error(`Quest not found: ${questId}`);
    if (quest.chainQuestId != null) {
      throw new Error(
        `Quest ${questId} holds on-chain escrow — only its creator can close or cancel it, ` +
          `because only the creator's wallet can trigger the refund`,
      );
    }
    if (MOCK_TASKS.some((t) => t.questId === questId && isTaskInFlight(t.status))) {
      throw new Error(
        `Quest ${questId} has work in flight — resolve or reject the open tasks before ending it`,
      );
    }
    return quest;
  }

  async adminCloseQuest(questId: string, _reason?: string): Promise<void> {
    await delay(MOCK_DELAY_MS);
    const quest = this.assertTerminable(questId);
    if (quest.status !== "active") {
      throw new Error(`Quest can only be closed from "active" status (current: ${quest.status})`);
    }
    quest.status = "closed";
    quest.updatedAt = new Date().toISOString();
  }

  async listUserNotificationChannels(userId: string): Promise<AdminNotificationChannel[]> {
    await delay(MOCK_DELAY_MS);
    if (!this.notificationChannels.has(userId)) {
      const profile = MOCK_USER_PROFILES[userId];
      this.notificationChannels.set(userId, profile?.email ? [{
        id: `mock-email-${userId}`,
        provider: "email",
        displayIdentifier: profile.email,
        optedIn: true,
        verifiedAt: profile.createdAt,
        revokedAt: null,
        revokedReason: null,
        createdAt: profile.createdAt,
        updatedAt: profile.createdAt,
      }] : []);
    }
    return [...(this.notificationChannels.get(userId) ?? [])];
  }

  async adminRevokeChannel(contactId: string): Promise<AdminChannelMutationResult> {
    await delay(MOCK_DELAY_MS);
    const channel = this.findNotificationChannel(contactId);
    if (!channel) return "not_found";
    if (channel.revokedAt) {
      return channel.revokedReason === "admin_revoked" ? "already_revoked" : "revoked_by_other";
    }
    channel.revokedAt = new Date().toISOString();
    channel.revokedReason = "admin_revoked";
    channel.updatedAt = channel.revokedAt;
    return "revoked";
  }

  async adminRestoreChannel(contactId: string): Promise<AdminChannelMutationResult> {
    await delay(MOCK_DELAY_MS);
    const channel = this.findNotificationChannel(contactId);
    if (!channel) return "not_found";
    if (!channel.revokedAt) return "already_active";
    if (channel.revokedReason !== "admin_revoked") return "not_admin_revoked";
    channel.revokedAt = null;
    channel.revokedReason = null;
    channel.updatedAt = new Date().toISOString();
    return "restored";
  }

  private findNotificationChannel(contactId: string): AdminNotificationChannel | undefined {
    for (const channels of this.notificationChannels.values()) {
      const channel = channels.find((candidate) => candidate.id === contactId);
      if (channel) return channel;
    }
    return undefined;
  }

  async adminCancelQuest(questId: string, _reason?: string): Promise<void> {
    await delay(MOCK_DELAY_MS);
    const quest = this.assertTerminable(questId);
    if (isTerminalQuestStatus(quest.status)) {
      throw new Error(`Quest cannot be cancelled from terminal status "${quest.status}"`);
    }
    quest.status = "cancelled";
    quest.updatedAt = new Date().toISOString();
  }
}
